// Pill row visualising the reminder schedule for an insurance/licence row.
// One pill per reminderDays value, sorted largest→smallest. Each pill is
// classified as fired (line-through, muted), next (most-recently-fired,
// brand violet), or future (upcoming, neutral). Records whose start date
// is in the future render every pill as future regardless of expiry.

interface ReminderSchedulePillsProps {
  today: Date;
  startDate: string;
  expiryDate: string;
  reminderDays: number[];
}

type PillState = "fired" | "next" | "future";

const STATE_CLASS: Record<PillState, string> = {
  fired:
    "bg-slate-100 text-slate-400 line-through dark:bg-slate-800 dark:text-slate-500",
  next:
    "border border-brand-400 bg-brand-50 text-brand-700 dark:border-brand-500 dark:bg-brand-950 dark:text-brand-300",
  future:
    "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
};

function isoToUtcMs(iso: string): number {
  const [y, m, d] = iso.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
}

function dateToIsoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export default function ReminderSchedulePills({
  today,
  startDate,
  expiryDate,
  reminderDays,
}: ReminderSchedulePillsProps) {
  if (!reminderDays || reminderDays.length === 0) return null;

  const todayIso = dateToIsoDay(today);
  const todayMs = isoToUtcMs(todayIso);
  const expiryMs = isoToUtcMs(expiryDate);
  const recordNotStarted = startDate > todayIso;

  const sorted = [...reminderDays].sort((a, b) => b - a);

  // Classify each pill as fired or future first; then promote the smallest
  // fired one to "next" so the user sees which reminder was most recent.
  const baseStates: PillState[] = sorted.map((days) => {
    if (recordNotStarted) return "future";
    const triggerMs = expiryMs - days * 86_400_000;
    return todayMs >= triggerMs ? "fired" : "future";
  });

  // The smallest fired pill (last "fired" in the largest-first ordering)
  // becomes "next" — it represents the most recently fired reminder.
  let nextIndex = -1;
  for (let i = 0; i < sorted.length; i += 1) {
    if (baseStates[i] === "fired") nextIndex = i;
  }
  const states: PillState[] = baseStates.map((s, i) =>
    i === nextIndex ? "next" : s,
  );

  return (
    <div className="flex flex-wrap gap-1.5">
      {sorted.map((days, i) => {
        const state = states[i];
        return (
          <span
            key={days}
            data-testid={`reminder-pill-${days}`}
            data-state={state}
            aria-label={`${days} days reminder, ${state}`}
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-caption font-medium ${STATE_CLASS[state]}`}
          >
            {days}d
          </span>
        );
      })}
    </div>
  );
}
